import type { Dispatch, SetStateAction } from 'react'

import { Button } from 'components/Button'
import { Icon } from 'components/Icon'
import { Textarea } from 'components/Textarea'
import {
  createEmptyReminder,
  DEFAULT_REMINDER_TIME,
  MAX_REMINDER_MESSAGE_LENGTH,
  type TSurveyBuilderReminder,
} from './builder'

const MAX_REMINDERS = 3

type Props = {
  reminders: TSurveyBuilderReminder[]
  setReminders: Dispatch<SetStateAction<TSurveyBuilderReminder[]>>
}

const sortRemindersByDays = (reminders: TSurveyBuilderReminder[]) =>
  [...reminders].sort((left, right) => left.days - right.days)

const numberInputClassName =
  'h-11 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30'

export function ReminderEditor({ reminders, setReminders }: Props) {
  const updateReminders = (
    update: (current: TSurveyBuilderReminder[]) => TSurveyBuilderReminder[],
  ) => {
    setReminders((current) => sortRemindersByDays(update(current)))
  }

  const updateReminder = (
    clientId: string,
    update: Partial<Pick<TSurveyBuilderReminder, 'days' | 'time' | 'message'>>,
  ) => {
    updateReminders((current) =>
      current.map((reminder) =>
        reminder.clientId === clientId ? { ...reminder, ...update } : reminder,
      ),
    )
  }

  const sortedReminders = sortRemindersByDays(reminders)

  return (
    <div className="grid gap-3">
      {sortedReminders.map((reminder, index) => (
        <div
          key={reminder.clientId}
          className="grid gap-3 rounded-xl border border-border/60 bg-surface/60 p-4"
        >
          <div className="flex items-end justify-between gap-2">
            <div className="flex flex-wrap items-end gap-3">
              <label className="grid max-w-[200px] gap-1.5 text-xs font-semibold text-ink">
                <span>配信からの経過日数</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={365}
                    className={numberInputClassName}
                    aria-label={`リマインド ${index + 1}の日数`}
                    value={reminder.days > 0 ? reminder.days : ''}
                    onChange={(event) =>
                      updateReminder(reminder.clientId, {
                        days:
                          event.target.value === ''
                            ? 0
                            : Number(event.target.value),
                      })
                    }
                  />
                  <span className="shrink-0 text-sm font-normal text-muted">
                    日後
                  </span>
                </div>
              </label>
              <label className="grid gap-1.5 text-xs font-semibold text-ink">
                <span>
                  配信から{reminder.days > 0 ? reminder.days : 'N'}日後の{' '}
                  {reminder.time || DEFAULT_REMINDER_TIME} に通知
                </span>
                <input
                  type="time"
                  className={numberInputClassName}
                  aria-label={`リマインド ${index + 1}の時刻`}
                  value={reminder.time || DEFAULT_REMINDER_TIME}
                  onChange={(event) =>
                    updateReminder(reminder.clientId, {
                      time: event.target.value || DEFAULT_REMINDER_TIME,
                    })
                  }
                />
              </label>
            </div>
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="shrink-0 text-danger"
              aria-label={`リマインド ${index + 1}を削除`}
              onClick={() =>
                updateReminders((current) =>
                  current.filter(
                    (currentReminder) =>
                      currentReminder.clientId !== reminder.clientId,
                  ),
                )
              }
            >
              <Icon icon="close" color="currentColor" width={16} height={16} />
            </Button>
          </div>
          <label className="grid gap-1.5 text-xs font-semibold text-ink">
            <span>リマインド文面</span>
            <Textarea
              className="min-h-[72px]"
              aria-label={`リマインド ${index + 1}の文面`}
              maxLength={MAX_REMINDER_MESSAGE_LENGTH}
              value={reminder.message}
              onChange={(event) =>
                updateReminder(reminder.clientId, {
                  message: event.target.value,
                })
              }
            />
            <span className="justify-self-end text-xs font-normal text-muted">
              {reminder.message.length} / {MAX_REMINDER_MESSAGE_LENGTH}文字
            </span>
          </label>
        </div>
      ))}
      {reminders.length < MAX_REMINDERS ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="justify-self-start"
          onClick={() =>
            updateReminders((current) => [...current, createEmptyReminder()])
          }
        >
          <Icon icon="plus" color="currentColor" width={14} height={14} className="mr-1" />
          リマインドを追加
        </Button>
      ) : null}
    </div>
  )
}
